import { useMemo, useState } from "react";

import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { alpha } from "@mui/material/styles";
import { withTranslation } from "react-i18next";

import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import StatPill from "@plannel/components/ui/StatPill";
import { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import { FS } from "@plannel/utils/font-util";

/*
  좌측 위치 목록 — 그래프에서 노드를 눈으로 찾는 대신 이름·코드로 찾아 들어오는 입구.

  위치가 수십 개를 넘어가면 그래프에서 특정 점포를 눈으로 훑어 찾는 건 사실상 불가능하다.
  검색 + 유형 필터로 후보를 줄이고, 행을 누르면 그래프가 그 노드를 선택 상태로 강조한다
  (필터가 아니라 강조라서 전체 그림 속 위치를 잃지 않는다).
*/
const { locationType } = CodeUtil;
const ALL = "__ALL__";

const DistributionNetworkLocationList = ({ t, network, selectedCd, onSelect }) => {
  const [keyword, setKeyword] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL);

  const nodes = useMemo(() => {
    // 그래프와 같은 순서(계층 → 사이트 → 코드)로 두고, 링크 없는 위치는 맨 뒤에 붙인다.
    const linked = network.tiers.flat();
    return [...linked, ...network.orphans];
  }, [network]);

  const typeCounts = useMemo(() => {
    const counts = new Map();
    for (const node of nodes) {
      const key = node.locType || "";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [nodes]);

  const filtered = useMemo(() => {
    const word = keyword.trim().toLowerCase();
    return nodes.filter(node => {
      if (typeFilter !== ALL && (node.locType || "") !== typeFilter) {
        return false;
      }
      if (!word) {
        return true;
      }
      return node.locCd.toLowerCase().includes(word) || node.name.toLowerCase().includes(word);
    });
  }, [nodes, keyword, typeFilter]);

  const chipSx = active => ({
    fontSize: FS.CAPTION, lineHeight: 1, px: 0.875, py: "4px", borderRadius: 999,
    cursor: "pointer", whiteSpace: "nowrap",
    bgcolor: active ? "primary.main" : "grey.100",
    color: active ? "primary.contrastText" : "text.secondary",
    fontWeight: active ? 600 : 400,
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1, display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ ...CARD_HEADER_SX, fontSize: FS.TITLE }}>{t("location")}</Box>
          <Box sx={{ flex: 1 }} />
          <StatPill label="" value={`${filtered.length} / ${nodes.length}`} sx={{ height: 19, px: 0.75, fontSize: FS.CAPTION }} />
        </Box>
        <TextField
          size="small"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          placeholder={`${t("locCd")} / ${t("locName")}`}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} />
              </InputAdornment>
            ),
            sx: { fontSize: FS.SUB, height: 30 },
          }}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          <Box sx={chipSx(typeFilter === ALL)} onClick={() => setTypeFilter(ALL)}>{t("all")}</Box>
          {[...typeCounts.entries()].map(([type, count]) => (
            <Box key={type || "none"} sx={chipSx(typeFilter === type)} onClick={() => setTypeFilter(type)}>
              {`${locationType?.[type] ? GridUtils.gridValueL10N(locationType[type].name) : t("none")} ${count}`}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 0.75, pb: 1 }}>
        {filtered.map(node => {
          const selected = node.locCd === selectedCd;
          return (
            <Box
              key={node.locCd}
              onClick={() => onSelect(node.locCd)}
              sx={{
                display: "flex", alignItems: "center", gap: 1,
                px: 1, py: 0.75, borderRadius: 1, cursor: "pointer",
                bgcolor: selected ? theme => alpha(theme.palette.primary.main, 0.08) : "transparent",
                "&:hover": { bgcolor: selected ? theme => alpha(theme.palette.primary.main, 0.08) : "action.hover" },
              }}
            >
              <Box sx={{
                width: 3, height: 22, borderRadius: 999, flexShrink: 0,
                bgcolor: ColorUtil.locationTypeColor(node.locType),
              }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ fontSize: FS.CAPTION, color: selected ? "primary.dark" : "text.disabled" }}>{node.locCd}</Box>
                <Box sx={{
                  fontSize: FS.SUB, fontWeight: selected ? 600 : 500,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {node.name}
                </Box>
              </Box>
              <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled", textAlign: "right", flexShrink: 0, lineHeight: 1.4 }}>
                <Box>{node.siteCd || "—"}</Box>
                <Box>{`${node.inbound.length} → ${node.outbound.length}`}</Box>
              </Box>
            </Box>
          );
        })}
        {!filtered.length && (
          <Box sx={{ px: 1.25, py: 2, fontSize: FS.SUB, color: "text.disabled" }}>{t("MSG_NoSearchData")}</Box>
        )}
      </Box>
    </Box>
  );
};

export default withTranslation()(DistributionNetworkLocationList);
